import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { ResPillComponent, ResPillStatus } from '../../../components/res-pill/res-pill.component';
import { ResultStatus, RunnerCase, RunnerStep } from '../../../models/test-run.model';
import { isFinalStatus } from '../../../services/execution-job.service';
import { formatDurationMs } from '../../../shared/duration.util';
import { isOpenStatus } from '../../../shared/test-run-outcome';
import { runnerPillStatus, runnerResultKey } from '../runner-status';
import { numberSteps } from '../step-numbering';

/** Was die Zustandsspalte zeigt: eine Pille, oder ohne Pille „Wartet". */
export interface RunnerCaseState {
  pill: { status: ResPillStatus; label: string; pulsing: boolean } | null;
  running: boolean;
  done: boolean;
  error: string | null;
}

interface StepView {
  id: string;
  number: string;
  depth: number;
  title: string;
  /** {@code null} bei einer Gruppenzeile — sie traegt kein eigenes Ergebnis. */
  pill: { status: ResPillStatus; label: string; pulsing: boolean } | null;
  duration: string;
}

interface CaseView {
  id: string;
  key: string;
  title: string;
  state: RunnerCaseState;
  duration: string;
  runnerName: string;
  steps: StepView[];
}

const NONE = '—';

function resultPill(status: ResultStatus): RunnerCaseState['pill'] {
  return { status: runnerPillStatus(status), label: runnerResultKey(status), pulsing: false };
}

/**
 * Der Zustand eines Falls im Rechner-Lauf. Der Auftrag des Rechners fuehrt, solange es ihn gibt;
 * ein Fall ohne Auftrag (Lauf vor NEX-1752) zeigt sein eigenes Ergebnis.
 */
export function runnerCaseState(runnerCase: RunnerCase): RunnerCaseState {
  const job = runnerCase.runnerJob;
  if (!job) {
    const open = isOpenStatus(runnerCase.status);
    return { pill: open ? null : resultPill(runnerCase.status), running: false, done: !open, error: null };
  }
  const done = isFinalStatus(job.status);
  switch (job.status) {
    case 'QUEUED':
      return { pill: null, running: false, done, error: null };
    case 'RUNNING':
      return {
        pill: { status: 'running', label: 'testCases.run.jobStatus.RUNNING', pulsing: true },
        running: true,
        done,
        error: null,
      };
    case 'ERROR':
      return {
        pill: { status: 'failed', label: 'testCases.run.jobStatus.ERROR', pulsing: false },
        running: false,
        done,
        error: job.error,
      };
    case 'CANCELLED':
      return {
        pill: { status: 'skipped', label: 'testCases.run.jobStatus.CANCELLED', pulsing: false },
        running: false,
        done,
        error: null,
      };
    default:
      // Der Endstand des Auftrags kommt vor dem Ergebnis des Falls an.
      return {
        pill: resultPill(isOpenStatus(runnerCase.status) ? job.status : runnerCase.status),
        running: false,
        done,
        error: null,
      };
  }
}

function caseDuration(runnerCase: RunnerCase, state: RunnerCaseState): string {
  if (!state.done) return NONE;
  const job = runnerCase.runnerJob;
  const start = Date.parse((job ? job.startedAt : runnerCase.startedAt) ?? '');
  const end = Date.parse((job ? job.finishedAt : runnerCase.completedAt) ?? '');
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return NONE;
  return formatDurationMs(end - start);
}

function stepViews(steps: RunnerStep[], running: boolean): StepView[] {
  const numbered = numberSteps(steps);
  const current = running
    ? numbered.find((entry) => !entry.isGroup && isOpenStatus(entry.step.actualStatus))?.step.id
    : undefined;
  return numbered.map(({ step, number, depth, isGroup }) => ({
    id: step.id,
    number,
    depth,
    title: step.title ?? step.description ?? NONE,
    pill: isGroup
      ? null
      : step.id === current
        ? { status: 'running', label: runnerResultKey('RUNNING'), pulsing: true }
        : resultPill(step.actualStatus),
    duration: isGroup ? '' : formatDurationMs(step.durationMs),
  }));
}

let nextListId = 0;

/**
 * Die Faelle eines Rechner-Laufs mit ihren Schritten (NEX-1754). Rein darstellend: den Stand
 * fuehrt die Live-Ansicht fort; hier liegt nur, welche Faelle die Person auf- oder zugeklappt hat.
 */
@Component({
  selector: 'app-runner-case-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, TranslatePipe, ResPillComponent],
  templateUrl: './runner-case-list.component.html',
  styleUrl: './runner-case-list.component.scss',
})
export class RunnerCaseListComponent {
  readonly cases = input.required<RunnerCase[]>();

  protected readonly idPrefix = `rcl-${nextListId++}`;

  /** Die Wahl der Person je Fall; ohne Wahl ist nur der laufende Fall offen. */
  private readonly choices = signal<ReadonlyMap<string, boolean>>(new Map());

  protected readonly views = computed<CaseView[]>(() =>
    this.cases().map((runnerCase) => {
      const state = runnerCaseState(runnerCase);
      return {
        id: runnerCase.id,
        key: runnerCase.key ?? NONE,
        title: runnerCase.title ?? NONE,
        state,
        duration: caseDuration(runnerCase, state),
        runnerName: runnerCase.runnerJob?.runnerName ?? NONE,
        steps: stepViews(runnerCase.steps, state.running),
      };
    }),
  );

  protected readonly done = computed(() => this.views().filter((view) => view.state.done).length);

  protected isExpanded(view: CaseView): boolean {
    return this.choices().get(view.id) ?? view.state.running;
  }

  protected toggle(view: CaseView): void {
    const open = !this.isExpanded(view);
    this.choices.update((choices) => new Map(choices).set(view.id, open));
  }

  protected stepsId(view: CaseView): string {
    return `${this.idPrefix}-steps-${view.id}`;
  }
}
